// src/components/developer/SystemToolsTab.js
// This tab contains backup tools, quick links, and system utilities

import React, { useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { getFirestore, doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { analyticsMeasurementId, analyticsReady, getAppCheckTokenValue, isAnalyticsEnabled } from "../../firebase";
import { logAuditEvent } from "../../utils/auditLogger";

const FEATURE_ACCESS_TABS = [
  {
    key: "orders",
    label: "Orders",
    description: "Lock Orders in nav and block route-level access unless allowlisted or paid.",
  },
  {
    key: "map",
    label: "Map",
    description: "Lock Map in nav and block territories/map access unless allowlisted or paid.",
  },
  {
    key: "adminSettings",
    label: "Admin Settings",
    description: "Lock only the first Settings tab inside Admin unless allowlisted or on a paid company state.",
  },
  {
    key: "connections",
    label: "Connections",
    description: "Lock the Admin Connections tab unless allowlisted or on a paid company state.",
  },
  {
    key: "sampleOrderMetrics",
    label: "Sample Order Metrics",
    description: "Lock the Metrics tab inside Sample Orders unless allowlisted or on a paid company state.",
  },
  {
    key: "accountAnalytics",
    label: "Account Analytics",
    description: "Lock the Analytics tab inside Accounts unless allowlisted or on a paid company state.",
  },
  {
    key: "inventoryMetrics",
    label: "Inventory Metrics",
    description: "Lock the Metrics tab inside Inventory unless allowlisted or on a paid company state.",
  },
  {
    key: "grids",
    label: "Grids",
    description: "Lock or remove the Grids workspace unless allowlisted or on a paid company state.",
  },
];

const buildFeatureInputState = () =>
  FEATURE_ACCESS_TABS.reduce((acc, tab) => {
    acc[tab.key] = "";
    return acc;
  }, {});

const pickGlobalDeveloperFlags = (featureFlags = {}) => ({
  hiddenEntirelyFeatures: featureFlags?.hiddenEntirelyFeatures || {},
  hiddenFeatures: featureFlags?.hiddenFeatures || {},
  paidOnlyFeatures: featureFlags?.paidOnlyFeatures || {},
  featureAllowEmails: featureFlags?.featureAllowEmails || {},
  featureLockMessages: featureFlags?.featureLockMessages || {},
  maintenanceBannerEnabled: !!featureFlags?.maintenanceBannerEnabled,
  maintenanceBannerMessage: (featureFlags?.maintenanceBannerMessage || "").toString(),
  maintenanceBannerColor: (featureFlags?.maintenanceBannerColor || "sky").toString(),
});

const hasDeveloperControlData = (featureFlags = {}) => {
  const picked = pickGlobalDeveloperFlags(featureFlags);
  return (
    Object.keys(picked.hiddenEntirelyFeatures || {}).length > 0 ||
    Object.keys(picked.hiddenFeatures || {}).length > 0 ||
    Object.keys(picked.paidOnlyFeatures || {}).length > 0 ||
    Object.keys(picked.featureAllowEmails || {}).length > 0 ||
    Object.keys(picked.featureLockMessages || {}).length > 0 ||
    picked.maintenanceBannerEnabled === true ||
    !!picked.maintenanceBannerMessage ||
    picked.maintenanceBannerColor !== "sky"
  );
};

const SystemToolsTab = () => {
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupResult, setBackupResult] = useState(null);

  const [filesBackupLoading, setFilesBackupLoading] = useState(false);
  const [filesBackupResult, setFilesBackupResult] = useState(null);

  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [featureFlags, setFeatureFlags] = useState({});
  const [controlsLoading, setControlsLoading] = useState(true);
  const [controlsSaving, setControlsSaving] = useState(false);
  const [bulkEmailLoading, setBulkEmailLoading] = useState(false);
  const [activeFeatureTab, setActiveFeatureTab] = useState("orders");
  const [featureAllowEmailsInput, setFeatureAllowEmailsInput] = useState(buildFeatureInputState);
  const [featureLockMessagesInput, setFeatureLockMessagesInput] = useState(buildFeatureInputState);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [maintenanceColor, setMaintenanceColor] = useState("sky");
  const [grandfatheredPricing, setGrandfatheredPricing] = useState(false);
  const [analyticsAvailable, setAnalyticsAvailable] = useState(false);
  const [bulkEmailForm, setBulkEmailForm] = useState({
    audience: "all_users",
    subject: "",
    message: "",
  });

  const PROJECT_ID = "deliveryapp-c1bf2";
  const REGION = "us-central1";
  const db = getFirestore();
  const functions = getFunctions(undefined, REGION);
  const developerControlsRef = useMemo(() => doc(db, "platformSettings", "developerControls"), [db]);

  // Buckets
  const BACKUP_BUCKET_NAME = `${PROJECT_ID}-firestore-backups`;
  const FIREBASE_STORAGE_BUCKET = `${PROJECT_ID}.appspot.com`;

  // Console links
  const GCS_BACKUPS_BUCKET_DIRECT = `https://console.cloud.google.com/storage/browser/${BACKUP_BUCKET_NAME}?project=${PROJECT_ID}`;
  const GCS_STORAGE_BUCKET_DIRECT = `https://console.cloud.google.com/storage/browser/${FIREBASE_STORAGE_BUCKET}?project=${PROJECT_ID}`;
  const GCS_BUCKET_LIST = `https://console.cloud.google.com/storage/browser?project=${PROJECT_ID}`;

  const colorClasses = {
    orange: "bg-orange-50 border-orange-200 hover:bg-orange-100 text-orange-900",
    blue: "bg-blue-50 border-blue-200 hover:bg-blue-100 text-blue-900",
    purple: "bg-purple-50 border-purple-200 hover:bg-purple-100 text-purple-900",
    yellow: "bg-yellow-50 border-yellow-200 hover:bg-yellow-100 text-yellow-900",
    indigo: "bg-indigo-50 border-indigo-200 hover:bg-indigo-100 text-indigo-900",
    green: "bg-green-50 border-green-200 hover:bg-green-100 text-green-900",
    gray: "bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-900",
  };

  // Small compact shortcut buttons
  const shortcutButtons = [
    {
      title: "Backups Bucket",
      url: GCS_BACKUPS_BUCKET_DIRECT,
      icon: "📦",
      color: "purple",
    },
    {
      title: "Firebase Storage",
      url: GCS_STORAGE_BUCKET_DIRECT,
      icon: "📁",
      color: "green",
    },
    {
      title: "Storage Buckets",
      url: GCS_BUCKET_LIST,
      icon: "🗂️",
      color: "gray",
    },
  ];

  const getIdTokenOrThrow = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("You must be logged in to run a backup.");
    }
    return await user.getIdToken(true);
  };

  useEffect(() => {
    let cancelled = false;
    analyticsReady.finally(() => {
      if (!cancelled) setAnalyticsAvailable(isAnalyticsEnabled());
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadControls = async () => {
      setControlsLoading(true);
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) {
          if (!cancelled) {
            setCompanyId("");
            setFeatureFlags({});
            setFeatureAllowEmailsInput(buildFeatureInputState());
            setFeatureLockMessagesInput(buildFeatureInputState());
            setMaintenanceMessage("");
            setMaintenanceColor("sky");
          }
          return;
        }

        const userSnap = await getDoc(doc(db, "users", currentUser.uid));
        const nextCompanyId = userSnap.exists() ? (userSnap.data()?.companyId || "") : "";
        if (!nextCompanyId) {
          if (!cancelled) {
            setCompanyId("");
            setFeatureFlags({});
            setGrandfatheredPricing(false);
          }
          return;
        }

        const [companySnap, globalControlsSnap] = await Promise.all([
          getDoc(doc(db, "companies", nextCompanyId)),
          getDoc(developerControlsRef),
        ]);
        const companyFlags = companySnap.exists() ? (companySnap.data()?.featureFlags || {}) : {};
        const companyData = companySnap.exists() ? (companySnap.data() || {}) : {};
        const globalFlags = globalControlsSnap.exists() ? (globalControlsSnap.data()?.featureFlags || {}) : {};
        let nextFlags = globalFlags;

        if (!hasDeveloperControlData(globalFlags) && hasDeveloperControlData(companyFlags)) {
          nextFlags = pickGlobalDeveloperFlags(companyFlags);
          await setDoc(developerControlsRef, {
            featureFlags: nextFlags,
            migratedFromCompanyId: nextCompanyId,
            updatedAt: serverTimestamp(),
          }, { merge: true });
        }

        if (!cancelled) {
          setCompanyId(nextCompanyId);
          setFeatureFlags(nextFlags);
          setGrandfatheredPricing(companyData?.grandfatheredPricing === true);
          setFeatureAllowEmailsInput(
            FEATURE_ACCESS_TABS.reduce((acc, tab) => {
              acc[tab.key] = ((nextFlags?.featureAllowEmails?.[tab.key] || [])).join(", ");
              return acc;
            }, {})
          );
          setFeatureLockMessagesInput(
            FEATURE_ACCESS_TABS.reduce((acc, tab) => {
              acc[tab.key] = (nextFlags?.featureLockMessages?.[tab.key] || "").toString();
              return acc;
            }, {})
          );
          setMaintenanceMessage((nextFlags?.maintenanceBannerMessage || "").toString());
          setMaintenanceColor((nextFlags?.maintenanceBannerColor || "sky").toString());
        }
      } catch (err) {
        console.error("[SystemToolsTab] Failed to load controls:", err);
        if (!cancelled) setError(err?.message || "Failed to load developer controls.");
      } finally {
        if (!cancelled) setControlsLoading(false);
      }
    };

    loadControls();
    return () => {
      cancelled = true;
    };
  }, [db, developerControlsRef]);

  const saveFeatureFlags = async (nextFlags) => {
    if (!companyId) throw new Error("No company found for this developer session.");
    setControlsSaving(true);
    setError("");
    setStatus("");
    try {
      await setDoc(developerControlsRef, {
        featureFlags: nextFlags,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setFeatureFlags(nextFlags);
      setStatus("✅ Global developer controls updated across all accounts.");
    } catch (err) {
      console.error("[SystemToolsTab] Failed to save feature flags:", err);
      setError(err?.message || "Failed to save developer controls.");
      throw err;
    } finally {
      setControlsSaving(false);
    }
  };

  const getFeatureToggleValue = (bucketKey, featureKey) => !!featureFlags?.[bucketKey]?.[featureKey];
  const maintenanceEnabled = !!featureFlags?.maintenanceBannerEnabled;
  const normalizedFeatureAllowEmails = useMemo(
    () =>
      FEATURE_ACCESS_TABS.reduce((acc, tab) => {
        acc[tab.key] = (featureAllowEmailsInput?.[tab.key] || "")
          .split(",")
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean);
        return acc;
      }, {}),
    [featureAllowEmailsInput]
  );
  const activeFeatureConfig = FEATURE_ACCESS_TABS.find((tab) => tab.key === activeFeatureTab) || FEATURE_ACCESS_TABS[0];
  const activeFeatureHiddenEntirely = getFeatureToggleValue("hiddenEntirelyFeatures", activeFeatureConfig.key);
  const activeFeatureHidden = getFeatureToggleValue("hiddenFeatures", activeFeatureConfig.key);
  const activeFeaturePaidOnly = getFeatureToggleValue("paidOnlyFeatures", activeFeatureConfig.key);

  const setFeatureToggle = (bucketKey, featureKey, value) => {
    setFeatureFlags((prev) => ({
      ...(prev || {}),
      [bucketKey]: {
        ...(prev?.[bucketKey] || {}),
        [featureKey]: value,
      },
    }));
  };

  const saveFeatureControls = async (featureKey) => {
    const beforeState = {
      hiddenEntirely: !!featureFlags?.hiddenEntirelyFeatures?.[featureKey],
      hidden: !!featureFlags?.hiddenFeatures?.[featureKey],
      paidOnly: !!featureFlags?.paidOnlyFeatures?.[featureKey],
      allowEmails: featureFlags?.featureAllowEmails?.[featureKey] || [],
      lockMessage: (featureFlags?.featureLockMessages?.[featureKey] || "").toString(),
    };
    const nextFlags = {
      ...(featureFlags || {}),
      hiddenEntirelyFeatures: {
        ...(featureFlags?.hiddenEntirelyFeatures || {}),
        [featureKey]: getFeatureToggleValue("hiddenEntirelyFeatures", featureKey),
      },
      hiddenFeatures: {
        ...(featureFlags?.hiddenFeatures || {}),
        [featureKey]: getFeatureToggleValue("hiddenFeatures", featureKey),
      },
      paidOnlyFeatures: {
        ...(featureFlags?.paidOnlyFeatures || {}),
        [featureKey]: getFeatureToggleValue("paidOnlyFeatures", featureKey),
      },
      featureAllowEmails: {
        ...(featureFlags?.featureAllowEmails || {}),
        [featureKey]: normalizedFeatureAllowEmails?.[featureKey] || [],
      },
      featureLockMessages: {
        ...(featureFlags?.featureLockMessages || {}),
        [featureKey]: (featureLockMessagesInput?.[featureKey] || "").toString().trim(),
      },
    };
    await saveFeatureFlags(nextFlags);
    try {
      const auth = getAuth();
      await logAuditEvent(auth.currentUser, {
        action: "feature_access_changed",
        entityType: "developerControls",
        entityId: featureKey,
        before: beforeState,
        after: {
          hiddenEntirely: !!nextFlags?.hiddenEntirelyFeatures?.[featureKey],
          hidden: !!nextFlags?.hiddenFeatures?.[featureKey],
          paidOnly: !!nextFlags?.paidOnlyFeatures?.[featureKey],
          allowEmails: nextFlags?.featureAllowEmails?.[featureKey] || [],
          lockMessage: (nextFlags?.featureLockMessages?.[featureKey] || "").toString(),
        },
        metadata: {
          featureKey,
          featureLabel:
            FEATURE_ACCESS_TABS.find((tab) => tab.key === featureKey)?.label || featureKey,
        },
        severity: "warning",
      });
    } catch (auditErr) {
      console.warn("[SystemToolsTab] Failed to audit feature access change:", auditErr);
    }
  };

  const saveMaintenanceBanner = async () => {
    const beforeState = {
      enabled: !!featureFlags?.maintenanceBannerEnabled,
      message: (featureFlags?.maintenanceBannerMessage || "").toString(),
      color: (featureFlags?.maintenanceBannerColor || "sky").toString(),
    };
    const nextFlags = {
      ...(featureFlags || {}),
      maintenanceBannerEnabled: maintenanceEnabled,
      maintenanceBannerMessage: maintenanceMessage,
      maintenanceBannerColor: maintenanceColor,
    };
    await saveFeatureFlags(nextFlags);
    try {
      const auth = getAuth();
      await logAuditEvent(auth.currentUser, {
        action: "maintenance_banner_changed",
        entityType: "developerControls",
        entityId: "maintenanceBanner",
        before: beforeState,
        after: {
          enabled: !!nextFlags?.maintenanceBannerEnabled,
          message: (nextFlags?.maintenanceBannerMessage || "").toString(),
          color: (nextFlags?.maintenanceBannerColor || "sky").toString(),
        },
        metadata: {
          changedByDeveloperTools: true,
        },
        severity: "warning",
      });
    } catch (auditErr) {
      console.warn("[SystemToolsTab] Failed to audit maintenance banner change:", auditErr);
    }
  };

  const saveCompanyBillingFlags = async () => {
    if (!companyId) {
      setError("No company loaded for this developer session.");
      return;
    }
    setControlsSaving(true);
    setError("");
    setStatus("");
    try {
      const companyRef = doc(db, "companies", companyId);
      const companySnap = await getDoc(companyRef);
      const previousGrandfatheredPricing =
        companySnap.exists() && companySnap.data()?.grandfatheredPricing === true;

      await setDoc(
        companyRef,
        {
          grandfatheredPricing: !!grandfatheredPricing,
          grandfatheredPricingUpdatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setStatus(
        grandfatheredPricing
          ? "✅ This company is now marked as grandfathered for lifetime pricing."
          : "✅ Grandfathered pricing was removed for this company."
      );
      try {
        const auth = getAuth();
        await logAuditEvent(auth.currentUser, {
          action: "company_billing_flag_changed",
          entityType: "company",
          entityId: companyId,
          before: {
            grandfatheredPricing: previousGrandfatheredPricing,
          },
          after: {
            grandfatheredPricing: !!grandfatheredPricing,
          },
          metadata: {
            flag: "grandfatheredPricing",
          },
          severity: "warning",
        });
      } catch (auditErr) {
        console.warn("[SystemToolsTab] Failed to audit company billing flag change:", auditErr);
      }
    } catch (err) {
      console.error("[SystemToolsTab] Failed to save company billing flags:", err);
      setError(err?.message || "Failed to save company billing flags.");
    } finally {
      setControlsSaving(false);
    }
  };

  const sendBulkAudienceEmail = httpsCallable(functions, "sendBulkAudienceEmail");

  const handleSendBulkEmail = async () => {
    if (!companyId) {
      setError("No company loaded for bulk email.");
      return;
    }
    if (!bulkEmailForm.subject.trim() || !bulkEmailForm.message.trim()) {
      setError("Subject and message are required.");
      return;
    }

    setBulkEmailLoading(true);
    setError("");
    setStatus("");
    try {
      const result = await sendBulkAudienceEmail({
        companyId,
        audience: bulkEmailForm.audience,
        subject: bulkEmailForm.subject.trim(),
        message: bulkEmailForm.message.trim(),
      });
      const data = result?.data || {};
      setStatus(`✅ Bulk email sent. Delivered: ${data.delivered || 0}. Failed: ${data.failed || 0}.`);
    } catch (err) {
      console.error("[SystemToolsTab] Bulk email failed:", err);
      setError(err?.message || err?.details || "Bulk email failed.");
    } finally {
      setBulkEmailLoading(false);
    }
  };

  const safeParseJson = (rawText) => {
    try {
      return rawText ? JSON.parse(rawText) : null;
    } catch {
      return { success: false, error: rawText || "Non-JSON response" };
    }
  };

  const buildProtectedHeaders = async (token) => {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const appCheckToken = await getAppCheckTokenValue();
    if (appCheckToken) {
      headers["X-Firebase-AppCheck"] = appCheckToken;
    }

    return headers;
  };

  // Trigger manual Firestore backup
  const triggerManualBackup = async () => {
    setBackupLoading(true);
    setStatus("");
    setError("");
    setBackupResult(null);

    try {
      const token = await getIdTokenOrThrow();
      const url = `https://${REGION}-${PROJECT_ID}.cloudfunctions.net/manualFirestoreBackup`;

      console.log("[DevTools] Triggering manual Firestore backup...");

      const response = await fetch(url, {
        method: "POST",
        headers: await buildProtectedHeaders(token),
      });

      const rawText = await response.text();
      const data = safeParseJson(rawText);

      console.log("[DevTools] Firestore backup response:", data);

      if (!response.ok) {
        setError(`❌ DB backup failed (${response.status}): ${data?.error || "Unknown error"}`);
        return;
      }

      if (data?.success) {
        setStatus("✅ Firestore backup initiated successfully! Open the Backups Bucket to view it.");
        setBackupResult(data);
      } else {
        setError(`❌ DB backup failed: ${data?.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("[DevTools] DB backup error:", err);
      setError(`❌ Could not trigger DB backup: ${err.message}`);
    } finally {
      setBackupLoading(false);
    }
  };

  // Trigger manual Storage (files) backup
  const triggerFilesBackup = async (opts = {}) => {
    setFilesBackupLoading(true);
    setStatus("");
    setError("");
    // Keep the previous filesBackupResult if continuing, otherwise clear it
    if (!opts?.continue) setFilesBackupResult(null);

    try {
      const token = await getIdTokenOrThrow();
      const url = `https://${REGION}-${PROJECT_ID}.cloudfunctions.net/manualStorageBackup`;

      const body = opts?.body || {};

      console.log("[DevTools] Triggering manual Storage backup...", body);

      const response = await fetch(url, {
        method: "POST",
        headers: await buildProtectedHeaders(token),
        body: JSON.stringify(body),
      });

      const rawText = await response.text();
      const data = safeParseJson(rawText);

      console.log("[DevTools] Storage backup response:", data);

      if (!response.ok) {
        setError(`❌ Files backup failed (${response.status}): ${data?.error || "Unknown error"}`);
        return;
      }

      if (data?.success) {
        setFilesBackupResult(data);

        if (data.partial && data.nextPageToken) {
          setStatus(
            `✅ Files backup started (partial). Copied ${data.filesCopied} files so far. Click "Continue Files Backup" to finish.`
          );
        } else {
          setStatus(`✅ Files backup completed! Copied ${data.filesCopied || 0} files.`);
        }
      } else {
        setError(`❌ Files backup failed: ${data?.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("[DevTools] Files backup error:", err);
      setError(`❌ Could not trigger files backup: ${err.message}`);
    } finally {
      setFilesBackupLoading(false);
    }
  };

  // Quick links configuration (existing tiles)
  const quickLinks = [
    {
      title: "Firebase Console",
      url: `https://console.firebase.google.com/project/${PROJECT_ID}/overview`,
      icon: "🔥",
      description: "Main Firebase console",
      color: "orange",
    },
    {
      title: "Firestore Database",
      url: `https://console.firebase.google.com/project/${PROJECT_ID}/firestore`,
      icon: "💾",
      description: "View database",
      color: "blue",
    },
    {
      title: "Cloud Functions",
      url: `https://console.firebase.google.com/project/${PROJECT_ID}/functions`,
      icon: "⚡",
      description: "View functions & logs",
      color: "yellow",
    },
    {
      title: "Stripe Dashboard",
      url: "https://dashboard.stripe.com",
      icon: "💳",
      description: "View payments",
      color: "indigo",
    },
    {
      title: "Authentication",
      url: `https://console.firebase.google.com/project/${PROJECT_ID}/authentication/users`,
      icon: "🔐",
      description: "Manage users",
      color: "green",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Status Messages */}
      {status && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2">
          {status}
        </div>
      )}
      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {/* Backups Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Backups</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Firestore exports + Firebase Storage file snapshots
            </p>
          </div>
        </div>

        {/* Compact shortcuts */}
        <div className="flex flex-wrap gap-2 mb-4">
          {shortcutButtons.map((btn) => (
            <a
              key={btn.title}
              href={btn.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1.5 border rounded-md transition-colors px-2 py-1 text-[11px] font-medium ${colorClasses[btn.color]}`}
              title={btn.url}
            >
              <span className="text-sm leading-none">{btn.icon}</span>
              <span className="leading-none">{btn.title}</span>
              <svg
                className="h-3.5 w-3.5 opacity-60"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          ))}
        </div>

        {/* Firestore Backup Row */}
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900">Firestore Backup</div>
            <div className="text-xs text-gray-500">
              Exports all collections into <span className="font-mono">{BACKUP_BUCKET_NAME}</span>
            </div>
          </div>

          <button
            onClick={triggerManualBackup}
            disabled={backupLoading}
            className="px-3 py-2 rounded-md bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-60 flex items-center gap-2 shrink-0"
          >
            {backupLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Backing up...
              </>
            ) : (
              <>
                <span className="text-sm">🗄️</span>
                Backup DB
              </>
            )}
          </button>
        </div>

        {/* Firestore Result */}
        {backupResult && (
          <div className="mb-4 p-3 rounded-lg bg-purple-50 border border-purple-200">
            <div className="text-sm font-medium text-purple-900 mb-2">
              Firestore Backup Details
            </div>
            <div className="text-xs text-purple-700 space-y-1">
              <div>
                <span className="font-mono">Bucket:</span> {backupResult.bucketName}
              </div>
              <div>
                <span className="font-mono">Path:</span> {backupResult.backupPrefix}
              </div>
              <div>
                <span className="font-mono">Time:</span> {backupResult.timestamp}
              </div>
            </div>
          </div>
        )}

        {/* Files Backup Row */}
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900">Files Backup (Firebase Storage)</div>
            <div className="text-xs text-gray-500">
              Copies <span className="font-mono">{FIREBASE_STORAGE_BUCKET}</span> into{" "}
              <span className="font-mono">{BACKUP_BUCKET_NAME}</span> under{" "}
              <span className="font-mono">firebase-storage-backups/</span>
            </div>
          </div>

          <button
            onClick={() => triggerFilesBackup({ continue: false, body: {} })}
            disabled={filesBackupLoading}
            className="px-3 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-2 shrink-0"
          >
            {filesBackupLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Backing up...
              </>
            ) : (
              <>
                <span className="text-sm">📁</span>
                Backup Files
              </>
            )}
          </button>
        </div>

        {/* Files Result */}
        {filesBackupResult && (
          <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
            <div className="text-sm font-medium text-emerald-900 mb-2">
              Files Backup Details
            </div>
            <div className="text-xs text-emerald-800 space-y-1">
              <div>
                <span className="font-mono">Source:</span> {filesBackupResult.sourceBucketName}
              </div>
              <div>
                <span className="font-mono">Dest Bucket:</span> {filesBackupResult.bucketName}
              </div>
              <div>
                <span className="font-mono">Path:</span> {filesBackupResult.backupPrefix}
              </div>
              <div>
                <span className="font-mono">Files Copied:</span> {filesBackupResult.filesCopied || 0}
              </div>

              {filesBackupResult.partial && filesBackupResult.nextPageToken && (
                <div className="pt-2 mt-2 border-t border-emerald-200">
                  <div className="text-[11px] text-emerald-800 mb-2">
                    Partial backup detected — click continue to finish copying remaining files.
                  </div>
                  <button
                    onClick={() =>
                      triggerFilesBackup({
                        continue: true,
                        body: {
                          backupPrefix: filesBackupResult.backupPrefix,
                          pageToken: filesBackupResult.nextPageToken,
                        },
                      })
                    }
                    disabled={filesBackupLoading}
                    className="px-3 py-1.5 rounded-md bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800 disabled:opacity-60"
                  >
                    Continue Files Backup
                  </button>
                </div>
              )}

              <div className="pt-2 mt-2 border-t border-emerald-200 text-[11px]">
                View in{" "}
                <a
                  href={`https://console.cloud.google.com/storage/browser/${BACKUP_BUCKET_NAME}/${filesBackupResult.backupPrefix}?project=${PROJECT_ID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-700 hover:text-emerald-900 underline"
                >
                  Backups Bucket →
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Info */}
        <div className="mt-4 p-3 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-600">
          <div className="font-medium text-gray-700 mb-1">ℹ️ Notes</div>
          <ul className="space-y-0.5 list-disc list-inside">
            <li>Firestore backups are exports (not human-readable files)</li>
            <li>Files backups copy your Firebase Storage bucket into a snapshot folder</li>
            <li>Both backups live in Google Cloud Storage</li>
          </ul>
        </div>
      </div>

      {/* Quick Links Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Links</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {quickLinks.map((link) => (
            <a
              key={link.url}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`p-3 rounded-lg border transition-colors ${colorClasses[link.color]}`}
            >
              <div className="flex items-start gap-2">
                <span className="text-2xl">{link.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{link.title}</div>
                  <div className="text-xs opacity-75 mt-0.5">{link.description}</div>
                </div>
                <svg
                  className="h-4 w-4 opacity-50"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Analytics Tracking */}
      <div className="bg-white border border-sky-200 rounded-lg p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Analytics Tracking</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Google Analytics status for this app and this browser session.
            </p>
          </div>
          <div
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${
              analyticsAvailable
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-amber-50 text-amber-700 border border-amber-200"
            }`}
          >
            {analyticsAvailable ? "Tracking enabled" : "Tracking unavailable"}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div className="p-3 rounded-lg bg-sky-50/60 border border-sky-100">
            <div className="text-slate-500 mb-1">Measurement ID</div>
            <div className="font-mono text-slate-900">{analyticsMeasurementId || "Not configured"}</div>
          </div>
          <div className="p-3 rounded-lg bg-sky-50/60 border border-sky-100">
            <div className="text-slate-500 mb-1">Browser Status</div>
            <div className={analyticsAvailable ? "font-medium text-emerald-700" : "font-medium text-amber-700"}>
              {analyticsAvailable ? "GA loaded and ready" : "Blocked, unsupported, or not initialized yet"}
            </div>
          </div>
        </div>
      </div>

      {/* System Info Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">System Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="text-gray-500 mb-1">Project ID</div>
            <div className="font-mono text-gray-900">{PROJECT_ID}</div>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="text-gray-500 mb-1">Functions Region</div>
            <div className="font-mono text-gray-900">{REGION}</div>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="text-gray-500 mb-1">Backups Bucket</div>
            <div className="font-mono text-gray-900 text-[10px]">{BACKUP_BUCKET_NAME}</div>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="text-gray-500 mb-1">Firebase Storage Bucket</div>
            <div className="font-mono text-gray-900 text-[10px]">{FIREBASE_STORAGE_BUCKET}</div>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="text-gray-500 mb-1">Google Analytics Measurement ID</div>
            <div className="font-mono text-gray-900">{analyticsMeasurementId || "Not configured"}</div>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="text-gray-500 mb-1">Analytics in This Browser</div>
            <div className={analyticsAvailable ? "font-medium text-emerald-700" : "font-medium text-amber-700"}>
              {analyticsAvailable ? "Enabled" : "Unavailable / blocked"}
            </div>
          </div>
        </div>
      </div>

      {/* Developer Controls */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-5">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Feature Access Controls</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Orders, Map, Admin tabs, and analytics tabs can each be hidden, made paid-only, or allowlisted by email.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-4">
          <div className="rounded-lg border border-slate-200 p-3 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm font-semibold text-slate-900">Feature Tabs</div>
                <div className="text-xs text-slate-500">Each section gets the same four controls.</div>
              </div>
              {controlsLoading ? <div className="text-xs text-slate-400">Loading…</div> : null}
            </div>

            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1 gap-1 flex-wrap">
              {FEATURE_ACCESS_TABS.map((tab) => {
                const isActive = tab.key === activeFeatureTab;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveFeatureTab(tab.key)}
                    className={[
                      "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                      isActive
                        ? "bg-white text-slate-900 shadow-sm border border-slate-200"
                        : "text-slate-600 hover:text-slate-900",
                    ].join(" ")}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className="rounded-lg border border-slate-200 p-3 space-y-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">{activeFeatureConfig.label}</div>
                <div className="text-xs text-slate-500">{activeFeatureConfig.description}</div>
              </div>

              <label className="flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50/70 px-3 py-2">
                <div>
                  <div className="text-sm font-medium text-slate-900">Hide in entirety for everyone</div>
                  <div className="text-xs text-slate-500">
                    Removes this feature completely from navigation and tabs for all users, including allowlisted emails.
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={activeFeatureHiddenEntirely}
                  onChange={(e) => setFeatureToggle("hiddenEntirelyFeatures", activeFeatureConfig.key, e.target.checked)}
                  className="rounded border-slate-300"
                />
              </label>

              <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2">
                <div>
                  <div className="text-sm font-medium text-slate-900">Hide for everyone</div>
                  <div className="text-xs text-slate-500">Shows this feature as gray with a lock icon instead of normal access.</div>
                </div>
                <input
                  type="checkbox"
                  checked={activeFeatureHidden}
                  onChange={(e) => setFeatureToggle("hiddenFeatures", activeFeatureConfig.key, e.target.checked)}
                  className="rounded border-slate-300"
                />
              </label>

              <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2">
                <div>
                  <div className="text-sm font-medium text-slate-900">Require paid plan</div>
                  <div className="text-xs text-slate-500">Trial users see this feature as locked unless allowlisted below.</div>
                </div>
                <input
                  type="checkbox"
                  checked={activeFeaturePaidOnly}
                  onChange={(e) => setFeatureToggle("paidOnlyFeatures", activeFeatureConfig.key, e.target.checked)}
                  className="rounded border-slate-300"
                />
              </label>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Always allow these emails</label>
                <input
                  type="text"
                  value={featureAllowEmailsInput?.[activeFeatureConfig.key] || ""}
                  onChange={(e) => setFeatureAllowEmailsInput((prev) => ({
                    ...(prev || {}),
                    [activeFeatureConfig.key]: e.target.value,
                  }))}
                  placeholder="roserobin13@gmail.com, owner@example.com"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Use comma-separated emails. Great for your own special login while everyone else is locked out.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Custom lock tooltip</label>
                <input
                  type="text"
                  value={featureLockMessagesInput?.[activeFeatureConfig.key] || ""}
                  onChange={(e) => setFeatureLockMessagesInput((prev) => ({
                    ...(prev || {}),
                    [activeFeatureConfig.key]: e.target.value,
                  }))}
                  placeholder="Temporarily unavailable while we finish updates."
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Leave blank to use the default tooltip for hidden or paid-only locks.
                </p>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => saveFeatureControls(activeFeatureConfig.key)}
                  disabled={controlsSaving || controlsLoading}
                  className="px-3 py-2 rounded-md bg-slate-900 text-white text-sm font-medium disabled:opacity-60"
                >
                  {controlsSaving ? "Saving…" : `Save ${activeFeatureConfig.label}`}
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3 space-y-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Maintenance Banner</div>
              <div className="text-xs text-slate-500">Shows a top banner in the app shell when you are working on things.</div>
            </div>

            <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2">
              <span className="text-sm font-medium text-slate-900">Enable banner</span>
              <input
                type="checkbox"
                checked={maintenanceEnabled}
                onChange={(e) => setFeatureFlags((prev) => ({ ...(prev || {}), maintenanceBannerEnabled: e.target.checked }))}
                className="rounded border-slate-300"
              />
            </label>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Banner message</label>
              <textarea
                rows={4}
                value={maintenanceMessage}
                onChange={(e) => setMaintenanceMessage(e.target.value)}
                placeholder="We are improving sample orders this afternoon. Some features may look different while we work."
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Banner color</label>
              <select
                value={maintenanceColor}
                onChange={(e) => setMaintenanceColor(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="sky">Sky</option>
                <option value="emerald">Emerald</option>
                <option value="amber">Amber</option>
                <option value="rose">Rose</option>
                <option value="violet">Violet</option>
                <option value="slate">Slate</option>
              </select>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={saveMaintenanceBanner}
                disabled={controlsSaving || controlsLoading}
                className="px-3 py-2 rounded-md bg-sky-600 text-white text-sm font-medium disabled:opacity-60"
              >
                {controlsSaving ? "Saving…" : "Save Banner"}
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 p-3 space-y-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">Company Billing Override</div>
            <div className="text-xs text-slate-500">
              Applies only to the company tied to your current session.
            </div>
          </div>

          <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2">
            <div>
              <div className="text-sm font-medium text-slate-900">Grandfathered pricing</div>
              <div className="text-xs text-slate-500">
                Shows that this company is one of the first 10 customers and their per-user pricing will not change while they remain active with SupplyHawk.
              </div>
            </div>
            <input
              type="checkbox"
              checked={grandfatheredPricing}
              onChange={(e) => setGrandfatheredPricing(e.target.checked)}
              className="rounded border-slate-300"
            />
          </label>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={saveCompanyBillingFlags}
              disabled={controlsSaving || controlsLoading || !companyId}
              className="px-3 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium disabled:opacity-60"
            >
              {controlsSaving ? "Saving…" : "Save Company Billing Flag"}
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Email */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Bulk Email</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Send a message to all users, admins, owners, admins and owners, end users, or customers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Audience</label>
            <select
              value={bulkEmailForm.audience}
              onChange={(e) => setBulkEmailForm((prev) => ({ ...prev, audience: e.target.value }))}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="all_users">All users</option>
              <option value="admins">All admins</option>
              <option value="owners">All owners</option>
              <option value="admins_owners">All admins and owners</option>
              <option value="end_users">End users</option>
              <option value="customers">Customers</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Subject</label>
            <input
              type="text"
              value={bulkEmailForm.subject}
              onChange={(e) => setBulkEmailForm((prev) => ({ ...prev, subject: e.target.value }))}
              placeholder="SupplyHawk update"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Message</label>
          <textarea
            rows={6}
            value={bulkEmailForm.message}
            onChange={(e) => setBulkEmailForm((prev) => ({ ...prev, message: e.target.value }))}
            placeholder="We are improving the app this afternoon. You may notice a few changes while we work."
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
          />
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSendBulkEmail}
            disabled={bulkEmailLoading || controlsLoading}
            className="px-3 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium disabled:opacity-60"
          >
            {bulkEmailLoading ? "Sending…" : "Send Bulk Email"}
          </button>
        </div>
      </div>

      {/* Future Tools Placeholder */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Additional Tools</h3>
        <p className="text-xs text-gray-500 mb-3">
          More utilities and testing tools will appear here as needed
        </p>
        <div className="text-xs text-gray-400 italic">
          • Test email notifications<br />
          • Clear cache<br />
          • Run data migrations<br />
          • Export reports<br />
        </div>
      </div>
    </div>
  );
};

export default SystemToolsTab;
